import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  XAxis,
  YAxis,
} from "recharts";
import { ChartTooltipV2, ChartTooltipV2Wrapper } from "@/components/ui/chartV2";
import { ChartDataPoint, ReferenceLineInfo, TickCallback } from "@/types/chart";
import { useChartDimensionsV2 } from "@/hooks/useChartDimensions";
import LegendWrapperV2 from "./components/legendWrapperV2";
import { formatAxisValue } from "@/lib/utils";
import type { HandleLegendItemChangeArguments } from "@/types/report";
import { SeriesConfig } from "@/common/type/report-types";
import { ReportChartSettingsV2 } from "@/common/report-configuration";

interface LineChartComponentProps {
  data: ChartDataPoint[]; // Objects of chart data points
  xAxis: string; // the data key for the x axis
  xAxisLabel: string; // label for the x axis
  yAxisLeftLabel: string; // label for the left y axis
  yAxisRightLabel: string; // label for the right y axis
  seriesConfig: SeriesConfig;
  chartSettings: ReportChartSettingsV2;
  maxHeight?: number;
  referenceLines?: ReferenceLineInfo[];
  handleLegendItemChange: (args: HandleLegendItemChangeArguments) => void;
}

const ChartContent: React.FC<{
  data: ChartDataPoint[];
  xAxis: string;
  seriesConfig: SeriesConfig;
  chartSettings: ReportChartSettingsV2;
  dimensions: ReturnType<typeof useChartDimensionsV2>;
  xAxisLabel: string;
  yAxisLeftLabel: string;
  yAxisRightLabel: string;
  referenceLines?: ReferenceLineInfo[];
}> = React.memo(
  ({
    data,
    xAxis,
    seriesConfig,
    chartSettings,
    dimensions,
    xAxisLabel,
    yAxisLeftLabel,
    yAxisRightLabel,
    referenceLines = [],
  }) => {
    const CustomXAxisTick = useCallback(({ x, y, payload }: TickCallback) => {
      const label = payload.value; // for a line chart, label is always a date label

      return (
        <g transform={`translate(${x},${y})`}>
          <text
            x={0}
            y={0}
            dy={10}
            dx={0}
            textAnchor="end"
            transform="rotate(-45)"
            style={{
              cursor: "pointer",
              fontSize: 14,
              fill: "#808080",
            }}
          >
            {label}
          </text>
        </g>
      );
    }, []);

    const uniqueAxisGroups = new Set<string>();
    seriesConfig.forEach((s) => {
      if (s.axisId) uniqueAxisGroups.add(s.axisId);
    });
    const axisGroups = Array.from(uniqueAxisGroups),
      hasRightAxis = axisGroups.length == 2;

    if (axisGroups.length > 2) {
      // note: this should never happen
      throw new Error("LineChartComponentV2 supports up to 2 Y axes");
    }

    const magnitude = function (num: number) {
      return Math.trunc(Math.abs(num)).toString().length - 1;
    };

    const calculateAxisGroupDomain = function (
      data: ChartDataPoint[],
      seriesConfig: SeriesConfig,
      axisId: string,
    ): [number, number] {
      let min = 0;
      let max = 0;
      seriesConfig.forEach((s) => {
        if (s.axisId === axisId) {
          data.forEach((d) => {
            const value = d[s.key];
            if (typeof value === "number") {
              if (value < min) min = value;
              if (value > max) max = value;
            }
          });
        }
      });
      const mag = Math.max(magnitude(max), magnitude(min));

      // For visual clarity, we round max up to the nearest integer multiple of its magnitude
      max = Math.ceil(max / Math.pow(10, mag)) * Math.pow(10, mag);
      min = Math.floor(min / Math.pow(10, mag)) * Math.pow(10, mag);
      return [min, max];
    };
    const axisGroup1Domain = calculateAxisGroupDomain(
      data,
      seriesConfig,
      axisGroups[0],
    );

    let axisGroup2Domain: [number, number] = [0, 0];
    if (axisGroups.length == 2) {
      axisGroup2Domain = calculateAxisGroupDomain(
        data,
        seriesConfig,
        axisGroups[1],
      );
      // If the max values of axis the domains are within 100% of each other, use the larger max for both
      const max1 = axisGroup1Domain[1];
      const max2 = axisGroup2Domain[1];
      if (
        (max1 > max2 && max1 / max2 <= 2) ||
        (max2 > max1 && max2 / max1 <= 2)
      ) {
        const unifiedMax = Math.max(max1, max2);
        axisGroup1Domain[1] = unifiedMax;
        axisGroup2Domain[1] = unifiedMax;
      }
    }

    const seriesConfigArray = Array.from(seriesConfig.values());
    return (
      <LineChart
        width={dimensions.chartWidth}
        height={dimensions.chartHeight}
        data={data}
        margin={dimensions.margin}
      >
        {seriesConfigArray
          .filter(
            (item) =>
              !item.key.endsWith("_projected") &&
              !chartSettings.legendItems[item.key]?.hidden,
          )
          .map((item) => {
            const projectedKey = `${item.key}_projected`;
            const projectedItem = seriesConfig.get(projectedKey);
            return (
              <React.Fragment key={item.label}>
                <Line
                  yAxisId={item.axisId}
                  type="monotone"
                  dataKey={item.key}
                  stroke={item.color}
                  name={item.label}
                  activeDot={{ r: 3, strokeWidth: 1, stroke: "black" }}
                  dot={{ r: 2 }}
                />
                {projectedItem && (
                  <Line
                    yAxisId={item.axisId}
                    key={`${projectedKey}p`}
                    type="monotone"
                    dataKey={projectedKey}
                    stroke={item.color}
                    name={`${item.label} (Projected)`}
                    activeDot={{ r: 3, strokeWidth: 1, stroke: "black" }}
                    strokeDasharray="5 5"
                    dot={{ r: 2 }}
                  />
                )}
              </React.Fragment>
            );
          })}
        <CartesianGrid strokeDasharray="3 3" />
        {referenceLines &&
          referenceLines.map((refLine) => (
            <ReferenceLine
              key={refLine.label}
              yAxisId={refLine.yAxisId}
              y={refLine.y}
              stroke="#BBB"
              label={{
                position: "top",
                value: refLine.label,
              }}
            />
          ))}
        <XAxis
          dataKey={xAxis}
          tick={CustomXAxisTick}
          interval={0}
          label={{
            value: xAxisLabel,
            position: "bottom",
            offset: 40,
          }}
        />
        <YAxis
          yAxisId={axisGroups[0]}
          label={{
            value: yAxisLeftLabel,
            angle: -90,
            position: "center",
            dx: -30,
            fontSize: 14,
            fontWeight: "bold",
          }}
          domain={axisGroup1Domain}
          tickFormatter={(value: number) => formatAxisValue(value)}
        />
        {hasRightAxis && (
          <YAxis
            yAxisId={axisGroups[1]}
            orientation="right"
            label={{
              value: yAxisRightLabel,
              angle: 90,
              position: "center",
              dx: 30,
              fontSize: 14,
              fontWeight: "bold",
            }}
            domain={axisGroup2Domain}
            tickFormatter={(value: number) => formatAxisValue(value)}
          />
        )}

        <ChartTooltipV2
          content={(props) => (
            <ChartTooltipV2Wrapper
              chartType="Line"
              seriesConfig={seriesConfig}
              showCount={chartSettings.showCount}
              {...props}
            />
          )}
          cursor={{ stroke: "#666", strokeWidth: 1 }}
        />
      </LineChart>
    );
  },
);

const LineChartComponentV2: React.FC<LineChartComponentProps> = (props) => {
  const {
    data,
    xAxis,
    seriesConfig,
    chartSettings,
    maxHeight,
    xAxisLabel,
    yAxisLeftLabel,
    yAxisRightLabel,
    handleLegendItemChange,
    referenceLines = [],
  } = props;

  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState<number>(800);

  // Measure container width on mount and resize
  useEffect(() => {
    if (!containerRef.current) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const width = entry.contentRect.width;
        if (width > 0) {
          setContainerWidth(width);
        }
      }
    });

    resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  const dimensions = useChartDimensionsV2(
    chartSettings,
    maxHeight,
    containerWidth,
  );

  return (
    <div
      ref={containerRef}
      style={{
        display: "flex",
        flexDirection: ["top", "bottom"].includes(
          chartSettings.legendPosition || "right",
        )
          ? "column"
          : "row",
        alignItems: "center",
        width: "100%",
      }}
    >
      {chartSettings.includeLegend &&
        ["left", "top"].includes(chartSettings.legendPosition || "right") && (
          <LegendWrapperV2
            position={chartSettings.legendPosition || "right"}
            items={seriesConfig}
            onItemChange={handleLegendItemChange}
          />
        )}

      <ChartContent
        data={data}
        xAxis={xAxis}
        seriesConfig={seriesConfig}
        chartSettings={chartSettings}
        dimensions={dimensions}
        xAxisLabel={xAxisLabel}
        yAxisLeftLabel={yAxisLeftLabel}
        yAxisRightLabel={yAxisRightLabel}
        referenceLines={referenceLines}
      />

      {chartSettings.includeLegend &&
        ["right", "bottom"].includes(
          chartSettings.legendPosition || "right",
        ) && (
          <LegendWrapperV2
            position={chartSettings.legendPosition || "right"}
            items={seriesConfig}
            onItemChange={handleLegendItemChange}
          />
        )}
    </div>
  );
};

export default React.memo(LineChartComponentV2) as typeof LineChartComponentV2;
